import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SettingCard } from "@/components/settings/setting-card";
import { CountrySelector } from "@/components/shared/country-selector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGoogleBusinessConnection } from "@/domains/settings/features/integrations/google-business/hooks/use-google-business-connection";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import type { BusinessSettingsFormData } from "../type/business-settings";
import { ContactInformationForm } from "./contact-manager";
import { SyncFromGoogleButton } from "./sync-from-google-button";

interface BusinessProfileFormProps {
  /** Loading state for initial data fetch */
  loading?: boolean;
  /** Saving state during mutations */
  saving?: boolean;
}

export function BusinessProfileForm({ loading = false, saving = false }: BusinessProfileFormProps) {
  const { t } = useTranslation("settings");
  const form = useFormContext<BusinessSettingsFormData>();
  const navigate = useNavigate();
  const { connection, loading: connectionLoading } = useGoogleBusinessConnection();
  const isGoogleConnected = Boolean(connection);

  if (loading) {
    return (
      <SettingCard title={t("business.title")} description={t("business.basicAccountInfo")}>
        <CenteredSpinner minHeight="min-h-[400px]" />
      </SettingCard>
    );
  }

  return (
    <SettingCard title={t("business.title")} description={t("business.basicAccountInfo")}>
      <div className="space-y-8">
        {/* Basic Information */}
        <div className="space-y-4">
          <div id="field-business-salon-name" className="space-y-2">
            <Label htmlFor="business-name" className="text-sm font-medium">
              {t("business.businessName")}
            </Label>
            <Controller
              control={form.control}
              name="basicInfo.name"
              rules={{ required: t("business.validation.nameRequired") }}
              render={({ field }) => (
                <Input
                  id="business-name"
                  type="text"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={(e) => {
                    field.onChange(e.target.value.trim());
                    field.onBlur();
                  }}
                  name={field.name}
                  ref={field.ref}
                  disabled={saving}
                  className="h-10"
                  placeholder={t("placeholders.enterName")}
                />
              )}
            />
            {form.formState.errors.basicInfo?.name && (
              <p className="text-sm text-destructive">
                {form.formState.errors.basicInfo.name.message}
              </p>
            )}
          </div>
        </div>

        {/* Address */}
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t("business.address")}
            </h3>
            <SyncFromGoogleButton />
          </div>

          <div id="field-business-address" className="space-y-2">
            <Label htmlFor="address" className="text-sm font-medium">
              {t("business.address")}
            </Label>
            <Controller
              control={form.control}
              name="basicInfo.address"
              rules={{ required: "Address is required" }}
              render={({ field }) => (
                <Input
                  id="address"
                  type="text"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={saving}
                  className="h-10"
                  placeholder={t("placeholders.enterAddress")}
                />
              )}
            />
            {!connectionLoading && !isGoogleConnected && (
              <div className="flex flex-col items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <p className="text-sm font-medium text-destructive">
                  The storefront map only appears for salons connected to Google Business Profile.
                  Without a connection, the storefront shows the address text only — no map.
                </p>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => navigate("/settings/google-business")}
                >
                  Connect Google Business
                </Button>
              </div>
            )}
            {form.formState.errors.basicInfo?.address && (
              <p className="text-sm text-destructive">
                {form.formState.errors.basicInfo.address.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="additionalAddressInfo" className="text-sm font-medium">
              Additional Address Info (Optional)
            </Label>
            <Controller
              control={form.control}
              name="basicInfo.additionalAddressInfo"
              render={({ field }) => (
                <Input
                  id="additionalAddressInfo"
                  type="text"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={saving}
                  className="h-10"
                  placeholder="Floor, apartment, suite, building, etc."
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium">
                {t("business.city")}
              </Label>
              <Controller
                control={form.control}
                name="basicInfo.city"
                render={({ field }) => (
                  <Input
                    id="city"
                    type="text"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    disabled={saving}
                    className="h-10"
                    placeholder={t("business.city")}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state" className="text-sm font-medium">
                {t("business.state")}
              </Label>
              <Controller
                control={form.control}
                name="basicInfo.state"
                render={({ field }) => (
                  <Input
                    id="state"
                    type="text"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    disabled={saving}
                    className="h-10"
                    placeholder={t("business.state")}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="zipCode" className="text-sm font-medium">
                {t("business.zipCode")}
              </Label>
              <Controller
                control={form.control}
                name="basicInfo.zipCode"
                render={({ field }) => (
                  <Input
                    id="zipCode"
                    type="text"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    disabled={saving}
                    className="h-10"
                    placeholder={t("business.zipCode")}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country" className="text-sm font-medium">
                {t("business.country")}
              </Label>
              <Controller
                control={form.control}
                name="basicInfo.country"
                render={({ field }) => (
                  <CountrySelector
                    id="country"
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                    disabled={saving}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div id="field-business-contact-info" className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {t("business.contactInfo")}
          </h3>

          <ContactInformationForm isDisabled={saving} />
        </div>
      </div>
    </SettingCard>
  );
}
